import { Box, Typography, TextField, Button, Divider, Skeleton, Stack } from '@mui/material';
import { Navigate } from 'react-router';
import { RouteMap } from '@/routes/Routes';
import { useAuthStore } from '@/stores/authStore';
import { hasBetaProductName } from '@/utils/subscriptionHelpers';
import { useCurrentPlanFeatures } from '@/hooks/useCurrentPlanFeatures';
import { useZapierWebhook } from '../hooks/useZapierWebhook';
import { useZapierFormatWebhooks } from '../hooks/useZapierFormatWebhooks';

const ZapierSettingsPage = () => {
  const { userData } = useAuthStore();
  const { betaFeatures, isLoading: isPlanLoading } = useCurrentPlanFeatures();
  const { value, onChange, onSave, isSaving, isDirty, isValid } = useZapierWebhook();
  const {
    formatTypes,
    isLoadingTypes,
    values: formatValues,
    onChange: onFormatChange,
    onSave: onFormatSave,
    isSaving: isFormatSaving,
    isDirty: isFormatDirty,
    allValid: allFormatValid,
    isValidUrl,
  } = useZapierFormatWebhooks();

  if (!hasBetaProductName(userData) && !betaFeatures.zapier && !isPlanLoading) {
    return <Navigate to={RouteMap.HOME} replace />;
  }

  const showError = value.trim() !== '' && !isValid;

  return (
    <Box
      px={{ xs: '16px', sm: '32px' }}
      py="24px"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '900px',
        mx: 'auto',
        gap: 3,
      }}
    >
      <Box>
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
          Zapier Integration
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 0.5 }}>
          Connect NoteGo to 5,000+ apps through Zapier. When a note is finalized, it will be sent to your Zapier webhook
          automatically.
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          To get started, create a Zap in Zapier using the &quot;Webhooks by Zapier&quot; trigger, copy the webhook URL,
          and paste it below.
        </Typography>
      </Box>

      {/* Main webhook URL — sends the full note */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          fullWidth
          label="Zapier Webhook URL"
          placeholder="https://hooks.zapier.com/hooks/catch/..."
          value={value}
          onChange={(e) => onChange(e.target.value)}
          error={showError}
          helperText={
            showError ? 'URL must start with https://hooks.zapier.com/' : 'Sends the full note to this webhook.'
          }
        />

        <Box>
          <Button
            variant="contained"
            onClick={() => {
              void onSave();
            }}
            disabled={!isDirty || isSaving || !isValid}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>

      <Divider />

      {/* Per-format webhook URLs */}
      <Box>
        <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
          Per-Format Webhook URLs
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          Optionally configure a separate webhook URL for each note format type. When you send a generated format to
          Zapier, it will use the format-specific webhook URL instead of the main one above.
        </Typography>

        {isLoadingTypes ? (
          <Stack spacing={2}>
            <Skeleton variant="rounded" height={56} />
            <Skeleton variant="rounded" height={56} />
            <Skeleton variant="rounded" height={56} />
          </Stack>
        ) : formatTypes.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            No format types available.
          </Typography>
        ) : (
          <Stack spacing={2}>
            {formatTypes.map((ft) => {
              const url = formatValues[ft.prompt_value] ?? '';
              const showFieldError = url.trim() !== '' && !isValidUrl(url);
              return (
                <TextField
                  key={ft.prompt_id}
                  fullWidth
                  label={`${ft.display_name} Webhook URL`}
                  placeholder="https://hooks.zapier.com/hooks/catch/..."
                  value={url}
                  onChange={(e) => onFormatChange(ft.prompt_value, e.target.value)}
                  error={showFieldError}
                  helperText={showFieldError ? 'URL must start with https://hooks.zapier.com/' : ''}
                />
              );
            })}
          </Stack>
        )}

        {formatTypes.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              onClick={() => {
                void onFormatSave();
              }}
              disabled={!isFormatDirty || isFormatSaving || !allFormatValid}
            >
              {isFormatSaving ? 'Saving...' : 'Save Format Webhooks'}
            </Button>
          </Box>
        )}
      </Box>

      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        Have feedback on this beta feature? Contact support and mention you&apos;re a beta tester.
      </Typography>
    </Box>
  );
};

export default ZapierSettingsPage;
